<template>
  <div class="user-create">
    <form
      @submit.prevent="submit"
      accept-charset="UTF-8"
      novalidate="novalidate"
    >
      <div class="row">
        <div
          class="col-sm-8 form-group has-feedback"
          :class="{ 'has-error': errors.has('User[email]') }"
        >
          <label for="user-email">Email</label>
          <input
            id="user-email"
            autocomplete="username"
            v-validate="'required'"
            v-model="user.email"
            required="required"
            name="User[email]"
            type="email"
            class="form-control"
            aria-required="true"
          />

          <span v-show="errors.has('User[email]')" class="help-block">
            {{ errors.first("User[email]") }}
          </span>
        </div>

        <div
          class="col-sm-4 form-group has-feedback"
          :class="{ 'has-error': errors.has('User[type]') }"
        >
          <label for="user-type">Type</label>
          <select
            id="user-type"
            v-validate="'required'"
            v-model.number="user.type"
            required="required"
            name="User[type]"
            class="form-control"
            aria-required="true"
          >
           <option value="" selected="selected">-</option>
           <option v-for="typeOption in this.typeOptions" :value="typeOption.key">
            {{typeOption.value}}
            </option> 
          </select>

          <span v-show="errors.has('User[type]')" class="help-block">
            {{ errors.first("User[type]") }}
          </span>
        </div>
      </div>
      <div class="row">
        <div
          class="col-sm-6 form-group has-feedback"
          :class="{ 'has-error': errors.has('User[first_name]') }"
        >
          <label for="user-first-name">First Name</label>
          <input
            id="user-first-name"
            v-validate="'required'"
            v-model="user.first_name"
            required="required"
            name="User[first_name]"
            type="text"
            class="form-control"
            aria-required="true"
          />

          <span v-show="errors.has('User[first_name]')" class="help-block">
            {{ errors.first("User[first_name]") }}
          </span>
        </div>

        <div
          class="col-sm-6 form-group has-feedback"
          :class="{ 'has-error': errors.has('User[last_name]') }"
        >
          <label for="user-last-name">Last Name</label>
          <input
            id="user-last-name"
            v-validate="'required'"
            v-model="user.last_name"
            required="required"
            name="User[last_name]"
            type="text"
            class="form-control"
            aria-required="true"
          />

          <span v-show="errors.has('User[last_name]')" class="help-block">
            {{ errors.first("User[last_name]") }}
          </span>
        </div>
      </div>
      <div class="row">
        <div
          class="col-sm-6 form-group has-feedback"
          :class="{ 'has-error': errors.has('User[phone]') }"
        >
          <label for="user-phone">Phone</label>
          <input
            id="user-phone"
            v-model="user.phone"
            name="User[phone]"
            type="text"
            class="form-control"
            aria-required="true"
          />

          <span v-show="errors.has('User[phone]')" class="help-block">
            {{ errors.first("User[phone]") }}
          </span>
          <span v-show="userPhoneInvalid" class="help-block">
            Invalid Phone Number - (Existing Resident Support Number)
          </span>
        </div>
        <div class="col-sm-6 form-group has-feedback" :class="{ 'has-error': userProjectInvalid }">
          <!-- Use v-show to avoid template conflicts-->
          <auto-complete
            @change="setProject"
            class="form-group"
            name="User[project_id]"
            label="Search Project"
            v-show="needsProjectAccess"
            search-results-index="Projects"
            find-result-index="Project"
            resource-url="/api/projects"
            ref="projectAutoComplete"
          >
            <template slot-scope="project" slot="model-display">
              {{project.name}}
            </template>
          </auto-complete>
          <span v-show="userProjectInvalid" class="help-block">
            Must assign project access for new resident or manager
          </span>
        </div>
      </div>

      <div class="row" v-if="!user.send_invite">
        <div
          class="col-sm-6 form-group has-feedback"
          :class="{ 'has-error': errors.has('User[password]') }"
        >
          <label for="user-password">Password</label>
          <input
            id="user-password"
            v-validate="'required|min:8'"
            v-model="user.password"
            autocomplete="new-password"
            name="User[password]"
            type="password"
            ref="password"
            class="form-control"
            aria-required="true"
          />

          <span v-show="errors.has('User[password]')" class="help-block">
            {{ errors.first("User[password]") }}
          </span>
        </div>
        <div
          class="col-sm-6 form-group has-feedback"
          :class="{ 'has-error': errors.has('User[password_confirm]') }"
        >
          <label for="user-password-confirm">Password Confirm</label>
          <input
            id="user-password-confirm"
            v-validate="'required|confirmed:password|min:8'"
            name="User[password_confirm]"
            v-model="user.password_confirm"
            autocomplete="new-password"
            type="password"
            class="form-control"
            aria-required="true"
          />

          <span
            v-show="errors.has('User[password_confirm]')"
            class="help-block"
          >
            {{ errors.first("User[password_confirm]") }}
          </span>
        </div>
      </div>

      <div class="form-group">
        <div class="checkbox">
          <label>
            <input
              id="user-send-invite"
              v-model.boolean="user.send_invite"
              name="User[send_invite]"
              type="checkbox"
            />
            Auto Generate Password (Send Invite)
          </label>
        </div>
      </div>

      <hr />

      <div class="row">
        <div class="col-sm-6 col-md-4 pull-right">
          <button
            type="submit"
            class="btn btn-success btn-lg btn-block"
            :disabled="loading"
          >
            Save
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>

const USER_TYPE_RESIDENT = 0;
const USER_TYPE_MANAGER = 1;

export default {
  props: {
    appUrl: {
        type: String,
        required: true,
    },
    typeOptions: {
      type: Array, 
      required: false,
    },
  },

  data() {
    return {
      user: {
        send_invite: false,
        project_id: null,
      },
      sendInvite: false,
      userPhoneInvalid: false,
      userProjectInvalid: false,
      loading: false,
    };
  },

  watch: {
    "user.send_invite"() {
      if (this.user.send_invite) {
        this.user.password = null;
        this.user.password_confirm = null;
      }
    },
  },

  computed: {
    needsProjectAccess() {
      return this.user.type === USER_TYPE_RESIDENT
        || this.user.type === USER_TYPE_MANAGER;
    },
    importForm() {
      var form = document.createElement("form");
      form.setAttribute("method", "POST");
      form.setAttribute("action", "/users");

      var hiddenField = document.createElement("input");
      hiddenField.setAttribute("type", "hidden");
      hiddenField.setAttribute("name", "_method");
      hiddenField.setAttribute("value", "POST");

      var token = $('meta[name="csrf-token"]').attr("content");
      var tokenField = document.createElement("input");
      tokenField.setAttribute("type", "hidden");
      tokenField.setAttribute("name", "_token");
      tokenField.setAttribute("value", token);

      form.appendChild(tokenField);
      form.appendChild(hiddenField);

      _.forIn(this.user, (value, key) => {
        if (value !== null) {
          let field = document.createElement("input");
          field.setAttribute("type", "hidden");
          field.setAttribute("name", "User[" + key + "]");
          field.setAttribute("value", value);
          form.appendChild(field);
        }
      });

      return form;
    },
  },

  methods: {
    validatePhone() {
      //Validate user phone number via call to user API
      //set route URL
    },
    setProject(project_id) {
      this.user.project_id = project_id
    },
    submit() {
      const form = this.importForm;
      this.userPhoneInvalid = false;
      this.userProjectInvalid = !!(this.needsProjectAccess && !this.user.project_id);

      return this.$validator.validateAll().then((valid) => {
        if (valid && !this.userProjectInvalid) {
          this.loading = true;
          
          const url = this.appUrl + '/api/users/validateUserPhoneNumber';
          const headers = {
            'Content-Type': 'application/json',
            'Phone': this.user.phone
          }
          axios.get(url, {
              headers: headers
            })
            .then((result) => {
              if (result.data.valid){
                document.body.appendChild(form);
                form.submit();
              } else {
                this.userPhoneInvalid = true;
              }
            })
            .catch(this.$root.handleErrors)
            .finally(data => {
              this.loading = false;
            })
        }
      });
    },
  },
};
</script>